import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  addPalmares,
  updatePalmares,
  deletePalmares,
  getPalmaresBySection,
} from "../../../../../services/palmaresServices";

import {
  buildPalmaresPayload,
  filterPalmares,
  getAvailableYears,
  getFallbackSectionId,
  getPalmaresStats,
  isDemoMode,
  sortPalmares,
} from "../utils/palmaresUtils";

export default function usePalmares(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [palmares, setPalmares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [drawer, setDrawer] = useState({
    type: null,
    palmares: null,
  });

  const demoMode = useMemo(() => isDemoMode(), []);

  const fetchPalmares = useCallback(async () => {
    if (!finalSectionId) {
      setPalmares([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const data = await getPalmaresBySection(finalSectionId);
      const safeData = Array.isArray(data) ? data : [];

      setPalmares(sortPalmares(safeData));
    } catch (error) {
      console.error("Erreur lors de la récupération des palmarès :", error);
      toast.error("Erreur lors de la récupération des palmarès.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchPalmares();
  }, [fetchPalmares]);

  const stats = useMemo(() => getPalmaresStats(palmares), [palmares]);

  const years = useMemo(() => getAvailableYears(palmares), [palmares]);

  const rows = useMemo(() => {
    return filterPalmares(palmares, searchTerm, yearFilter, statusFilter);
  }, [palmares, searchTerm, yearFilter, statusFilter]);

  const handleDemoClick = () => {
    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return true;
    }

    return false;
  };

  const openCreateDrawer = () => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "create",
      palmares: null,
    });
  };

  const openEditDrawer = (selectedPalmares) => {
    if (handleDemoClick()) return;

    setDrawer({
      type: "edit",
      palmares: selectedPalmares,
    });
  };

  const closeDrawer = () => {
    setDrawer({
      type: null,
      palmares: null,
    });
  };

  const handleCreate = async (formData) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = buildPalmaresPayload(formData, finalSectionId);

      await addPalmares(payload);

      toast.success("Palmarès créé avec succès.");
      await fetchPalmares();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la création du palmarès :", error);
      toast.error("Erreur lors de la création du palmarès.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (palmaresId, formData, originalPalmares) => {
    if (handleDemoClick()) return;

    try {
      setSaving(true);

      const payload = {
        ...originalPalmares,
        ...buildPalmaresPayload(formData, finalSectionId),
        id: palmaresId,
      };

      await updatePalmares(payload);

      toast.success("Palmarès modifié avec succès.");
      await fetchPalmares();
      closeDrawer();
    } catch (error) {
      console.error("Erreur lors de la modification du palmarès :", error);
      toast.error("Erreur lors de la modification du palmarès.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (palmaresId) => {
    if (handleDemoClick()) return;

    const confirmed = window.confirm("Supprimer ce palmarès ?");

    if (!confirmed) return;

    try {
      await deletePalmares(palmaresId);

      setPalmares((prev) =>
        prev.filter((item) => Number(item.id) !== Number(palmaresId))
      );

      toast.success("Palmarès supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression du palmarès :", error);
      toast.error("Erreur lors de la suppression du palmarès.");
    }
  };

  return {
    finalSectionId,
    palmares,
    rows,
    stats,
    years,
    loading,
    saving,
    demoMode,

    searchTerm,
    setSearchTerm,
    yearFilter,
    setYearFilter,
    statusFilter,
    setStatusFilter,

    drawer,
    openCreateDrawer,
    openEditDrawer,
    closeDrawer,

    fetchPalmares,
    handleCreate,
    handleUpdate,
    handleDelete,
  };
}